import { useMemo, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { TIMEZONE_LIST } from '../../../constants';
import { useUser } from '../../../contexts/UserContext';
import Title from '../../elements/title/Title';

const StyledMenuWrapper = styled('div')({
  height: 'calc(100% - 350px)',
  padding: 0,
  '& .MuiList-padding': {
    paddingTop: 0,
  },
});
const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  display: 'flex',
  paddingTop: theme.spacing(2),
  background: theme.palette.common.white,
  paddingBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)({
  backgroundColor: 'inherit',
  width: '96%',
});
const StyledSelect = styled(Select)({
  padding: 0,
});

const TimezoneForm = ({ onSubmit }) => {
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);

  const { user } = useUser();
  const { t } = useTranslation();

  const handleTimezoneChange = async (e) => {
    const { name, value } = e.target;
    if (!value) {
      e.stopPropagation();
      return;
    }
    setOpen(false);
    await onSubmit(name, value);
  };

  const matchedOptions = useMemo(
    () =>
      TIMEZONE_LIST.filter((option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      ),
    [searchText]
  );

  const select = (
    <StyledSelect
      value={user?.timeZone}
      name="timeZone"
      inputProps={{ 'aria-label': 'timezone select' }}
      fullWidth
      onClick={() => setOpen(false)}
      onChange={handleTimezoneChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(e) => {
        const isHeader = e.currentTarget
          .getAttribute('class')
          ?.includes('MuiListSubheader');
        if (!isHeader) {
          setOpen(false);
          setTimeout(() => setSearchText(''), 1);
        }
      }}
      // Disable auto focus on MenuItems to allow TextField to be in focus
      // Set anchorOrigin to display menu in fix position
      MenuProps={{
        PaperProps: { component: StyledMenuWrapper },
        autoFocus: false,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        getcontentanchorel: null,
      }}
    >
      <StyledListSubheader>
        <StyledTextField
          size="small"
          variant="outlined"
          placeholder={t('searchTimezone', { ns: 'fields' })}
          autoFocus
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </StyledListSubheader>
      {matchedOptions.map(({ label, zoneId }) => (
        <MenuItem key={zoneId} value={zoneId}>
          {label}
        </MenuItem>
      ))}
    </StyledSelect>
  );

  return (
    <>
      <Title subTitle text={t('timezoneSettings', { ns: 'sectionTitles' })} />
      <Grid container spacing={1} alignItems="flex-end">
        <Grid style={{ paddingLeft: 20, paddingRight: 20 }} size={12}>
          {select}
        </Grid>
      </Grid>
    </>
  );
};

TimezoneForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default TimezoneForm;
